import React, { useEffect, useState, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import { compactNaira, naira, fmtDateTime } from "../../lib/format";
import { HC } from "../hr/theme";
import { Card, SectionHead, Btn, Table, Field, Input, Select, Empty } from "../hr/ui";
import { Spinner, Tabs, download, Delta } from "./mui";
import { Modal } from "./TasksChecklists";
import { FileDown, FileText, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const DIMS = [["location", "Location"], ["day", "Day"], ["month", "Month"], ["product", "Product"],
  ["channel", "Channel"], ["order_type", "Order Type"], ["customer", "Customer"]];

export default function ManagementReports() {
  const { locationId, period, locations } = useOutletContext();
  const [tab, setTab] = useState("dimension");
  const [dims, setDims] = useState(["location"]);
  const [metric, setMetric] = useState("revenue");
  const [report, setReport] = useState(null);
  const [proc, setProc] = useState(null);
  const [scheduled, setScheduled] = useState([]);
  const [generated, setGenerated] = useState([]);
  const [form, setForm] = useState(null);

  const qs = { period, location_id: locationId || undefined, metric, dimensions: dims.join(",") };

  const load = useCallback(() => {
    api.get("/management/reports/dimension", { params: { period, location_id: locationId || undefined, metric, dimensions: dims.join(",") } })
      .then(setReport).catch((e) => toast.error(formatApiError(e)));
    api.get("/management/reports/procurement", { params: { period: "this_month", location_id: locationId || undefined } })
      .then(setProc).catch(() => {});
    api.get("/management/scheduled-reports").then(setScheduled).catch(() => {});
    api.get("/management/generated-reports").then(setGenerated).catch(() => {});
  }, [period, locationId, metric, dims]);

  useEffect(() => { load(); }, [load]);

  const toggleDim = (d) => setDims((cur) => cur.includes(d) ? (cur.length > 1 ? cur.filter((x) => x !== d) : cur) : [...cur, d]);

  const exportReport = (fmt) => {
    const p = new URLSearchParams({ period, metric, dimensions: dims.join(","), ...(locationId ? { location_id: locationId } : {}) });
    download(`/management/reports/exports/dimension.${fmt}?${p}`, `management-report.${fmt}`);
  };

  const saveSchedule = async () => {
    try {
      await api.post("/management/scheduled-reports", {
        ...form, dimensions: form.dimensions, recipients: form.recipients.split(",").map((s) => s.trim()).filter(Boolean) });
      toast.success("Report scheduled"); setForm(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (id) => {
    try { await api.del(`/management/scheduled-reports/${id}`); toast.success("Schedule removed"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (!report) return <Spinner />;

  return (
    <div className="space-y-5" data-testid="mgmt-reports-page">
      <Tabs testid="reports-tabs" value={tab} onChange={setTab}
        tabs={[["dimension", "Sales & Margin"], ["procurement", "Procurement"], ["scheduled", "Scheduled Exports"]]} />

      {tab === "dimension" && (
        <Card testid="dimension-report-card">
          <SectionHead title="Dimension Report" sub={`${report.label} · ${report.rows.length} rows · ${naira(report.totals.revenue)} across ${report.totals.orders} orders`}
            right={
              <div className="flex gap-2">
                <Btn variant="gold" onClick={() => exportReport("csv")} data-testid="report-export-csv"><FileDown size={15} /> CSV</Btn>
                <Btn variant="primary" onClick={() => exportReport("pdf")} data-testid="report-export-pdf"><FileText size={15} /> PDF</Btn>
              </div>
            } />
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <span className="text-[10px] font-bold uppercase tracking-[0.14em]" style={{ color: HC.soft }}>Dimensions</span>
            {DIMS.map(([v, l]) => (
              <button key={v} onClick={() => toggleDim(v)} data-testid={`report-dim-${v}`}
                className="h-8 px-3 rounded-full text-[12px] font-semibold border"
                style={dims.includes(v) ? { background: HC.burgundy, color: "#fff", borderColor: HC.burgundy }
                  : { background: "#fff", color: HC.text, borderColor: HC.border }}>{l}</button>
            ))}
            <div className="flex-1" />
            <Select value={metric} onChange={(e) => setMetric(e.target.value)} className="w-40" data-testid="report-metric-select">
              {["revenue", "orders", "quantity", "aov"].map((m) => <option key={m} value={m}>{`Sort by ${m}`}</option>)}
            </Select>
          </div>
          <Table testid="dimension-report-table" columns={[
            ...report.dimensions.map((d) => ({ key: d, label: d.replace(/_/g, " ").toUpperCase() })),
            { key: "revenue", label: "Revenue", align: "right", render: (r) => compactNaira(r.revenue) },
            { key: "orders", label: "Orders", align: "right" },
            { key: "quantity", label: "Qty", align: "right" },
            { key: "aov", label: "AOV", align: "right", render: (r) => compactNaira(r.aov) },
          ]} rows={report.rows.slice(0, 300)} rowKey={(r, i) => i} empty="No data for this period" />
        </Card>
      )}

      {tab === "procurement" && (
        <Card testid="procurement-report-card">
          <SectionHead title="Procurement Spend" sub={proc ? `${proc.totals.orders} POs · ${naira(proc.totals.spend)} this month` : ""} />
          <Table testid="procurement-report-table" columns={[
            { key: "supplier", label: "Supplier" },
            { key: "orders", label: "POs", align: "right" },
            { key: "open", label: "Open", align: "right" },
            { key: "spend", label: "Spend", align: "right", render: (r) => compactNaira(r.spend) },
          ]} rows={proc?.rows || []} rowKey={(r, i) => i} empty="No purchase orders in this period" />
        </Card>
      )}

      {tab === "scheduled" && (
        <div className="space-y-5">
          <Card testid="scheduled-reports-card">
            <SectionHead title="Scheduled Exports" sub="Generated automatically by the platform cron"
              right={<Btn variant="primary" data-testid="new-schedule-btn"
                onClick={() => setForm({ name: "", metric: "revenue", dimensions: "location", period: "last_7", frequency: "daily", format: "csv", recipients: "" })}>
                <Plus size={15} /> New Schedule</Btn>} />
            <Table testid="scheduled-reports-table" columns={[
              { key: "name", label: "Name" },
              { key: "dimensions", label: "Dimensions" },
              { key: "period", label: "Period" },
              { key: "frequency", label: "Frequency" },
              { key: "format", label: "Format" },
              { key: "recipients", label: "Recipients", render: (r) => (r.recipients || []).join(", ") || "—" },
              { key: "last_run_at", label: "Last run", render: (r) => r.last_run_at ? fmtDateTime(r.last_run_at) : "Never" },
              { key: "act", label: "", align: "right", render: (r) => (
                <Btn variant="danger" onClick={() => del(r.id)} data-testid={`schedule-delete-${r.id}`}><Trash2 size={13} /></Btn>
              ) },
            ]} rows={scheduled} empty="No scheduled exports" />
          </Card>

          <Card testid="generated-reports-card">
            <SectionHead title="Generated Reports" sub="Download previously generated runs" />
            {generated.length ? (
              <Table testid="generated-reports-table" columns={[
                { key: "name", label: "Report" },
                { key: "row_count", label: "Rows", align: "right" },
                { key: "generated_at", label: "Generated", render: (r) => fmtDateTime(r.generated_at) },
                { key: "act", label: "", align: "right", render: (r) => (
                  <div className="flex gap-2 justify-end">
                    <Btn variant="gold" onClick={() => download(`/management/generated-reports/${r.id}/download.csv`, `${r.name}.csv`)} data-testid={`generated-csv-${r.id}`}>CSV</Btn>
                    <Btn variant="primary" onClick={() => download(`/management/generated-reports/${r.id}/download.pdf`, `${r.name}.pdf`)} data-testid={`generated-pdf-${r.id}`}>PDF</Btn>
                  </div>
                ) },
              ]} rows={generated} empty="Nothing generated yet" />
            ) : <Empty text="No generated reports yet — the cron will populate this" />}
          </Card>
        </div>
      )}

      {form && (
        <Modal title="Schedule a report" onClose={() => setForm(null)} onSave={saveSchedule} testid="schedule-modal">
          <Field label="Name"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="schedule-name-input" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Dimensions (comma separated)"><Input value={form.dimensions} onChange={(e) => setForm({ ...form, dimensions: e.target.value })} /></Field>
            <Field label="Metric">
              <Select value={form.metric} onChange={(e) => setForm({ ...form, metric: e.target.value })}>
                {["revenue", "orders", "quantity", "aov"].map((m) => <option key={m} value={m}>{m}</option>)}
              </Select>
            </Field>
            <Field label="Period">
              <Select value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })}>
                {["today", "last_7", "this_month", "last_month", "last_30"].map((p) => <option key={p} value={p}>{p}</option>)}
              </Select>
            </Field>
            <Field label="Frequency">
              <Select value={form.frequency} onChange={(e) => setForm({ ...form, frequency: e.target.value })} data-testid="schedule-frequency-select">
                {["daily", "weekly", "monthly"].map((f) => <option key={f} value={f}>{f}</option>)}
              </Select>
            </Field>
            <Field label="Format">
              <Select value={form.format} onChange={(e) => setForm({ ...form, format: e.target.value })}>
                <option value="csv">CSV</option><option value="pdf">PDF</option>
              </Select>
            </Field>
            <Field label="Recipients (comma separated)"><Input value={form.recipients} onChange={(e) => setForm({ ...form, recipients: e.target.value })} /></Field>
          </div>
        </Modal>
      )}
    </div>
  );
}
